import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
}

/**
 * Reusable dropdown wrapping Angular Material's `mat-select` with the shared
 * `admin-standard-select-panel` styling and a chevron baked in (Material's own
 * arrow is hidden globally in dropdown.scss, so every select ships its own).
 *
 * Replaces the 200+ hand-wired `<mat-select>` blocks across the admin, each of
 * which currently repeats its own `<mat-select-trigger>` + `#xxxArrowDrop`
 * chevron and validation markup.
 *
 * Pass a `FormControl` via `[control]`, options via `[options]`. Emits
 * `(selectionChange)` with the new value.
 */
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
})
export class SelectComponent implements OnInit {
  @Input() control!: FormControl;
  @Input() options: any[] = [];
  /** Property names to read on each option object (defaults suit {value,label}). */
  @Input() valueKey = 'value';
  @Input() labelKey = 'label';
  @Input() multiple = false;
  @Input() placeholder = '';
  /**
   * Fixed trigger text (translate key) shown regardless of selection — the
   * multi-select convention where the field keeps its prompt ("Choose
   * categories") and the chosen items are listed by the caller underneath.
   * When set it also overrides the single-select trigger.
   */
  @Input() triggerLabel = '';
  /** Leading empty option label (single-select). Omit to hide the empty option. */
  @Input() emptyLabel = '';
  /** Value bound to the empty option (default empty string, the app's convention). */
  @Input() emptyValue: any = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() isInvalid = false;
  @Input() errorText = '';
  /** Set false to suppress app-select's own invalid-feedback (when the host
   *  component renders validation errors itself, e.g. app-form-input-field). */
  @Input() showValidation = true;
  /** Contextual message (translate key) shown under the control, independent
   *  of validation — e.g. a "no results" / "no available slots" notice. */
  @Input() warningText = '';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() inputId = '';
  /** Optional data-testid applied to the mat-select for e2e targeting. */
  @Input() testId: string | null = null;
  /** Optional data-testid applied to EVERY flat <mat-option> (same value on
   *  each), preserving legacy per-option e2e selectors. Null = no attribute. */
  @Input() optionTestId: string | null = null;
  @Input() panelClass = 'admin-standard-select-panel';
  @Input() customValidationMessages: { [key: string]: string } = {};
  /**
   * Hint rendered under the select as the app's `.explanation`. Translate key;
   * pre-translate at the call site for parameterized hints. Empty = hidden, so
   * conditional hints bind `[helpText]="cond ? 'key' : ''"`.
   */
  @Input() helpText = '';

  /** Emits Material's MatSelectChange so existing handlers ($event.value) work unchanged. */
  @Output() selectionChange = new EventEmitter<MatSelectChange>();

  /** Forwards the panel open/close state (mat-select's openedChange) so callers
   *  that saved on close (e.g. the admin-payment-options multi-select) keep working. */
  @Output() openedChange = new EventEmitter<boolean>();

  /**
   * Grouped options rendered as `mat-optgroup`s after the flat `options`
   * (e.g. the loyalty product picker's category-grouped offers). Group label
   * and child-options arrays are read via `groupLabelKey`/`groupOptionsKey`
   * so callers can bind domain objects directly.
   */
  @Input() optionGroups: any[] = [];
  @Input() groupLabelKey = 'label';
  @Input() groupOptionsKey = 'options';

  private pendingSelectedValues: any;

  /**
   * Standalone usage (no caller FormControl): reflect an externally-managed
   * selection — e.g. ids derived from a FormArray. Mirrors
   * app-searchable-multi-select's `[selectedValues]`; pair with
   * `(selectionChange)` to write changes back.
   */
  @Input() set selectedValues(v: any) {
    if (v === undefined) {
      return;
    }
    if (this.control) {
      this.control.setValue(v, { emitEvent: false });
    } else {
      this.pendingSelectedValues = v;
    }
  }

  @ViewChild(MatSelect) private matSelect?: MatSelect;

  /** Closes the dropdown panel. Material's default scroll strategy is
   *  `reposition()`, so an open panel follows the trigger and stays open while
   *  the page scrolls; screens that would rather dismiss it need a way to say
   *  so, which the raw `mat-select` they used to hold gave them for free.
   *
   *  Side effect worth knowing: if the panel WAS open, MatSelect.close() runs
   *  its onTouched callback, so a bound control becomes touched — enough to
   *  reveal a required-field error on a field the user never typed in. Harmless
   *  when the panel is already shut, and harmless without a `control` binding. */
  close(): void {
    this.matSelect?.close();
  }

  ngOnInit(): void {
    if (!this.control) {
      this.control = new FormControl(this.multiple ? [] : this.emptyValue);
    }
    if (this.pendingSelectedValues !== undefined) {
      this.control.setValue(this.pendingSelectedValues, { emitEvent: false });
      this.pendingSelectedValues = undefined;
    }
    if (!this.inputId) {
      this.inputId = 'select-' + Math.random().toString(36).substring(2, 11);
    }
  }

  groupLabel(group: any): string {
    return group?.[this.groupLabelKey] ?? '';
  }

  groupOptions(group: any): any[] {
    return group?.[this.groupOptionsKey] ?? [];
  }

  /** Track groups by label — callers may rebuild the array each CD pass. */
  trackByGroupLabel = (_index: number, group: any): any =>
    this.groupLabel(group);

  get sizeClass(): string {
    if (this.size === 'sm') {
      return 'form-control-sm';
    }
    if (this.size === 'lg') {
      return 'form-control-lg';
    }
    return '';
  }

  /** Disable the greyed `.default-option` look for selects where 0/'' are real values. */
  @Input() greyWhenEmpty = true;

  /**
   * Exact values that should render greyed like a placeholder, replacing the
   * default empty-ish set — e.g. `['1']` for a select whose untouched default
   * is the string '1'.
   */
  @Input() greyValues: any[] | null = null;

  /** Greys the value like the app's `.default-option` when nothing real is chosen. */
  get isDefaultValue(): boolean {
    if (!this.greyWhenEmpty) {
      return false;
    }
    const v = this.control?.value;
    if (this.greyValues) {
      return this.greyValues.includes(v);
    }
    // 'null' (string) is a legacy no-selection sentinel — several screens
    // initialize controls with it and pass emptyValue="null" (an attribute
    // binding, i.e. the literal string). Without it here those triggers
    // render their empty label in the dark value colour instead of the
    // placeholder grey (category form Stations / Top-Level Category).
    return v === '' || v == null || v === 0 || v === '0' || v === 'null';
  }

  get showError(): boolean {
    return (
      this.isInvalid ||
      !!(
        this.control?.invalid &&
        (this.control?.dirty || this.control?.touched)
      )
    );
  }

  optValue(option: any): any {
    return option?.[this.valueKey];
  }

  /**
   * Track options by value so callers may bind `[options]` to a getter that
   * rebuilds the array each change-detection pass. Without this, ngFor
   * recreates every mat-option per pass and mat-select's async selection
   * re-init schedules another pass — an infinite loop that freezes the page.
   */
  trackByValue = (_index: number, option: any): any => this.optValue(option);

  optLabel(option: any): any {
    return option?.[this.labelKey];
  }

  /**
   * Optional caller template for rich option content (icons, multi-line labels,
   * conditional dates). Declared in the caller as
   * `<ng-template #appSelectOption let-option>…</ng-template>`; when present it
   * renders each option AND the single-select trigger, so screens no longer
   * hand-roll a `<mat-select-trigger>` to show composed option text.
   */
  @ContentChild('appSelectOption', { read: TemplateRef })
  optionTemplate: TemplateRef<any> | null = null;

  /** The currently-selected option object (single-select) for the trigger. */
  get selectedOption(): any {
    const v = this.control?.value;
    return this.options.find((o) => this.optValue(o) === v) ?? null;
  }

  /** Label shown in the custom trigger for the current value. */
  get selectedLabel(): string {
    const v = this.control?.value;
    const option = this.options.find((o) => this.optValue(o) === v);
    return (
      this.optLabel(option) ||
      this.emptyLabel ||
      this.placeholder ||
      'admin.global.select'
    );
  }

  get errorMessageKey(): string {
    const errors = this.control?.errors;
    if (!errors) {
      return '';
    }
    const custom = this.customValidationMessages || {};
    if (errors['required']) {
      return custom['required'] || 'admin.global.field.required';
    }
    const firstKey = Object.keys(errors)[0];
    return custom[firstKey] || 'admin.global.field.invalid';
  }

  onSelectionChange(event: MatSelectChange): void {
    this.selectionChange.emit(event);
  }
}
